import { useState, type ReactNode } from 'react';
import { PictureOutlined } from '@ant-design/icons';

export interface ImageWithFallbackProps {
  src: string | null | undefined;
  alt: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  /** Shown instead of the picture while `src` is missing or broken (default: a picture icon). */
  fallbackIcon?: ReactNode;
}

/** Lazy-loaded image with a placeholder icon while missing/broken (docs/PLAN.md §6). */
export function ImageWithFallback({
  src,
  alt,
  width,
  height,
  className,
  fallbackIcon,
}: ImageWithFallbackProps) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        className={className}
        role="img"
        aria-label={alt}
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-bg-layout)',
          color: 'var(--color-text-tertiary)',
          fontSize: 24,
        }}
      >
        {fallbackIcon ?? <PictureOutlined />}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading="lazy"
      className={className}
      style={{ objectFit: 'cover' }}
      onError={() => setFailed(true)}
    />
  );
}
